import type { GridSortModel } from "@mui/x-data-grid";
import { logger } from "@sentry/react";
import type { Tables, TablesInsert, TablesUpdate } from "@shared/database.types.ts";
import type { EventDetails } from "@shared/supabase.types.ts";
import useSWR, { type SWRConfiguration } from "swr";
import { z } from "zod";
import { supabaseClient } from "@/lib/supabase";
import type { AdminBookingStatus } from "@/routes/admindashboard/_layout/events";
import { EVENTS_CACHE_KEYS } from "./cache-keys";
export type EventSummary = Tables<"event_summary">;

type EventSupa = Tables<"events">;
type EventSupaInsert = TablesInsert<"events">;
type EventSupaUpdate = TablesUpdate<"events">;

const AttendanceStatus = z.enum(["ATTENDED", "NOT_ATTENDED", "CANCELLED"]).nullable();
const AttendanceParticipantStatus = z.enum(["attended", "not_attended"]).nullable();
const ParticipantRole = z.enum(["student", "parent", "tutor", "observer"]);
const TransactionType = z.enum(["CHARGE", "PAYOUT"]);
const TransactionStatus = z.enum(["PAID", "UNPAID"]);
const CategoryType = z.enum(["Peer Tutoring"]);
export const EventSummaryParticipantSchema = z.object({
  role: z.enum(["tutor", "parent", "student"]),
  user_id: z.uuid().nullable(),
  staff_id: z.uuid().nullable(),
  last_name: z.string().nullable(),
  first_name: z.string().nullable(),
  attendance: z.enum(["attended", "not_attended", "excused", "late"]),
  participant_id: z.int(),
});
export const EventSummaryParticipantsSchema = z.array(EventSummaryParticipantSchema);

const UserSchema = z.object({
  user_id: z.uuid(),
  email: z.email().nullable(),
  first_name: z.string(),
  last_name: z.string(),
  cognito_sub: z.string().uuid().nullable(),
  phone: z.string().nullable(),
  is_child: z.boolean(),
  is_parent: z.boolean(),
  is_active: z.boolean(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  has_onboarded: z.boolean(),
  grade: z.string().nullable(),
  school: z.string().nullable(),
  crm_notes: z.string().nullable(),
  grade_updated_at: z.string().nullable(),
  subjects: z.array(z.string()).nullable(),
});

const StaffSchema = z.object({
  staff_id: z.string().uuid(),
  grade: z.string().nullable(),
  is_remote: z.boolean(),
  is_in_person: z.boolean(),
  is_searchable: z.boolean(),
  location: z.string().nullable(),
  resume_url: z.url().nullable(),
  transcript_url: z.url().nullable(),
  photo_url: z.string().nullable(),
  bio: z.string().nullable(),
  school_attended: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }).nullable(),
  updated_at: z.iso.datetime({ offset: true }).nullable(),
  is_admin: z.boolean(),
  is_tutor: z.boolean(),
  cognito_sub: z.string().uuid(),
  email: z.email(),
  phone: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  hourly_rate: z.number(),
  is_active: z.boolean(),
});

const SimpleStaffSchema = z.object({
  staff_id: z.string().uuid(),
  grade: z.string().nullable(),
  photo_url: z.string().nullable(),
  first_name: z.string(),
  last_name: z.string(),
});

const EventParticipantSchema = z.object({
  participant_id: z.number().int(),
  event_id: z.number().int(),
  user_id: z.uuid().nullable(),
  staff_id: z.uuid().nullable(),
  role: ParticipantRole,
  attendance: AttendanceParticipantStatus,
  created_at: z.iso.datetime({ offset: true }),
  users: UserSchema.nullable(),
  staff: StaffSchema.nullable(),
});

const TransactionSchema = z.object({
  transaction_id: z.number().int(),
  amount: z.number(),
  description: z.string(),
  transaction_type: TransactionType,
  charged_at: z.iso.datetime({ offset: true }),
  notes: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  payment_id: z.number().int().nullable(),
  status: TransactionStatus,
});

const EventTransactionLinkSchema = z.object({
  link_id: z.number().int(),
  event_id: z.number().int(),
  transaction_id: z.number().int(),
  amount_applied: z.number(),
  created_at: z.iso.datetime({ offset: true }),
  transactions: TransactionSchema,
});

const ServiceTypeSchema = z.object({
  service_type_id: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  base_price: z.number(),
  base_staff_rate: z.number(),
  min_duration: z.number().int(),
  max_duration: z.number().int(),
  is_active: z.boolean(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  tutor_can_add_discount: z.boolean(),
  category: CategoryType,
});

const EventDetailsSchema = z.object({
  event_id: z.number().int(),
  title: z.string(),
  description: z.string(),
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }),
  attendance: AttendanceStatus,
  is_taught: z.boolean(),
  url: z.string().nullable(),
  location: z.string().nullable(),
  public_notes: z.string().nullable(),
  private_notes: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  code: z.string(),
  service_type_id: z.number().int(),
  service_types: ServiceTypeSchema,
  event_participants: z.array(EventParticipantSchema),
  event_transaction_links: z.array(EventTransactionLinkSchema),
});

export const EventPayloadSchema = z.array(EventDetailsSchema);

export interface EventsDetailsFilters {
  userId?: string;
  staffId?: string;
  before?: string;
  after?: string;
  limit?: number;
  skip?: number;
}

// SWR configuration with deduplication and revalidation settings
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 600000, // 10 minute
  focusThrottleInterval: 30000, // 30 seconds
  errorRetryCount: 3,
  shouldRetryOnError: true,
};

// --- Standalone CRUD Functions for Events (these still hit the 'events' table) ---

export async function createEvent(input: EventSupaInsert) {
  const { data: newEvent, error } = await supabaseClient
    .from("events")
    .insert(input)
    .select()
    .single();

  if (error) {
    console.error("Error creating event", error);
    throw error;
  }
  return newEvent;
}

export async function updateEvent(input: EventSupaUpdate) {
  if (!input.event_id) {
    throw new Error("event_id is required to update an event");
  }
  const { data: updatedEvent, error } = await supabaseClient
    .from("events")
    .update(input)
    .eq("event_id", input.event_id)
    .select()
    .single();

  if (error) {
    console.error("Error updating event", error);
    throw error;
  }
  return updatedEvent;
}

export async function deleteEvent(id: number) {
  const { error } = await supabaseClient.from("events").delete().eq("event_id", id);

  if (error) {
    console.error("Error deleting event", error);
    throw error;
  }
}

// --- Fetch Functions (mostly use 'event_summary' view) ---

/**
 * Fetches a single raw event directly from the events table.
 * @param id Event ID
 * @returns Promise<EventSupa | null>
 */
export async function fetchEventById(id: number): Promise<EventSupa | null> {
  const { data, error } = await supabaseClient
    .from("events")
    .select("*")
    .eq("event_id", id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching event by id:", error);
    if (error.code !== "PGRST116") {
      throw error;
    }
  }
  return data;
}

/**
 * Fetches paginated and filtered events from the summary view.
 */
export async function getPaginatedEvents(options: {
  page: number;
  pageSize: number;
  status?: AdminBookingStatus;
  userId?: string;
  staffId?: string;
}) {
  const { page, pageSize, status, userId, staffId } = options;
  const rangeFrom = (page - 1) * pageSize;
  const rangeTo = rangeFrom + pageSize - 1;

  let query = supabaseClient.from("event_summary").select("*", { count: "exact" });

  if (userId) {
    query = query.eq("user_id", userId);
  }
  if (staffId) {
    query = query.eq("staff_id", staffId);
  }
  if (status) {
    console.warn("Filtering by status is not yet implemented in getPaginatedEvents.");
  }

  query = query.range(rangeFrom, rangeTo).order("start_date", { ascending: false });

  const { data, error, count } = await query;

  if (error) {
    console.error("Error fetching paginated events", error);
    throw error;
  }

  return { events: data ?? [], count: count ?? 0 };
}

/**
 * Fetches all participants for a single event from the summary view.
 * @param id Event ID
 * @returns Promise<EventSummary[]>
 */
export async function fetchEventSummaryById(id: number): Promise<EventSummary[]> {
  const { data, error } = await supabaseClient.from("event_summary").select("*").eq("event_id", id);

  if (error) {
    console.error("Error fetching event summary by id", error);
    throw error;
  }
  return data ?? [];
}

/**
 * Fetches events for a specific user from the summary view.
 */
export async function fetchEventsByUserId(
  userId: string,
  startDate?: string,
  endDate?: string,
): Promise<EventSummary[]> {
  let query = supabaseClient.from("event_summary").select().contains("user_ids", [userId]);

  if (startDate) {
    query = query.gte("start_date", startDate);
  }
  if (endDate) {
    query = query.lte("end_date", endDate);
  }

  const { data, error } = await query.order("start_date", { ascending: false });

  if (error) {
    console.error("Error fetching events by user id", error);
    throw error;
  }

  // iterate throuhg each event, remove participants.user_id that do not match the userId param
  // TODO optimize this in the supabase query if possible
  if (data) {
    data.forEach((event) => {
      if (event.participants) {
        event.participants = Array.isArray(event.participants)
          ? event.participants.filter(
              (participant) =>
                typeof participant === "object" &&
                participant !== null &&
                "user_id" in participant &&
                participant.user_id === userId,
            )
          : event.participants;
      }
    });
  }

  return data ?? [];
}

/**
 * Fetches events for a specific staff member from the summary view.
 */
export async function fetchEventsByStaffId(staffId: string, startDate?: string, endDate?: string) {
  let query = supabaseClient.from("event_summary").select().contains("staff_ids", [staffId]);

  if (startDate) {
    query = query.gte("start_date", startDate);
  }
  if (endDate) {
    query = query.lte("end_date", endDate);
  }

  const { data, error } = await query.order("start_date", { ascending: false });
  if (error) {
    console.error("Error fetching events by staff id", error);
    throw error;
  }

  return data ?? [];
}

/**
 * Fetches events by transaction ID from the summary view.
 */
export async function fetchEventsByTransactionId(transactionId: number): Promise<EventSummary[]> {
  const { data, error } = await supabaseClient
    .from("event_summary")
    .select("*")
    .contains("transaction_ids", [transactionId]);

  if (error) {
    console.error("Error fetching events by transaction id", error);
    throw error;
  }

  return data ?? [];
}

// --- New Fetch Functions for Detailed Events (from events.ts) ---

const eventDetailsSelect = `
  *,
  event_participants (
    *,
    users (*),
    staff (*)
  ),
  event_transaction_links (
    *,
    transactions (*)
  ),
  service_types (*)
`;

export async function fetchEventDetails(eventId: number): Promise<EventDetails | null> {
  const { data, error } = await supabaseClient
    .from("events")
    .select(eventDetailsSelect)
    .eq("event_id", eventId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching event details:", error);
    if (error.code !== "PGRST116") {
      // Ignore no rows found for single()
      throw error;
    }
  }
  return data;
}

export async function listEventsDetails(ids: number[]): Promise<EventDetails[] | null> {
  const { data, error } = await supabaseClient
    .from("events")
    .select(eventDetailsSelect)
    .in("event_id", ids);

  if (error) {
    console.error("Error fetching event details:", error);
    if (error.code !== "PGRST116") {
      // Ignore no rows found for single()
      throw error;
    }
  }
  return data;
}

export async function fetchEventsDetailsByIds(eventIds: number[]): Promise<EventDetails[]> {
  if (!eventIds || eventIds.length === 0) {
    return [];
  }

  const { data, error } = await supabaseClient
    .from("events")
    .select(eventDetailsSelect)
    .in("event_id", eventIds);

  if (error) {
    console.error("Error fetching event details by IDs:", error);
    throw error;
  }

  return data ?? [];
}

export async function fetchEventsDetails(filters: EventsDetailsFilters): Promise<EventDetails[]> {
  let query = supabaseClient.from("events").select(eventDetailsSelect);

  if (filters.userId || filters.staffId) {
    const participantQuery = supabaseClient.from("event_participants").select("event_id");

    if (filters.userId) {
      participantQuery.eq("user_id", filters.userId);
    }
    if (filters.staffId) {
      participantQuery.eq("staff_id", filters.staffId);
    }
    if (filters.limit || filters.skip) {
      const page = filters.limit ?? 1000;
      const start = filters.skip ?? 0;
      const end = start + page;

      participantQuery.range(start, end);
    }
    const { data: eventIdsData, error: eventIdsError } = await participantQuery;

    if (eventIdsError) {
      console.error("Error fetching event_ids for filtering", eventIdsError);
      throw eventIdsError;
    }

    const eventIds = eventIdsData.map((e) => e.event_id);
    if (eventIds.length === 0) return []; // No events match filter

    query = query.in("event_id", eventIds).order("start_date", { ascending: false });
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching events details:", error);
    throw error;
  }
  return data ?? [];
}

// --- SWR Hooks (Updated) ---

export function usePaginatedEvents(options: {
  page: number;
  pageSize: number;
  status?: AdminBookingStatus;
  userId?: string;
  staffId?: string;
}) {
  const { page, pageSize, status, userId, staffId } = options;
  const cacheKey = [
    EVENTS_CACHE_KEYS.EVENTS,
    "paginated",
    page,
    pageSize,
    status,
    userId,
    staffId,
  ].join("-");

  const { data, error, isLoading, mutate } = useSWR(
    cacheKey,
    () => getPaginatedEvents({ page, pageSize, status, userId, staffId }),
    swrConfig,
  );

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
}

/**
 * Hook for accessing a single raw event by ID for admin purposes.
 * @param id Event ID
 */
export function useEvent(id: number) {
  const { data, error, isLoading, mutate } = useSWR<EventSupa | null>(
    id ? `event-${id}` : null,
    () => fetchEventById(id),
    swrConfig,
  );

  const handleUpdateEvent = async (input: EventSupaUpdate) => {
    await updateEvent(input);
    mutate();
  };

  const handleDeleteEvent = async () => {
    await deleteEvent(id);
    mutate(null, false);
  };

  return {
    event: data,
    isLoading,
    error,
    refreshEvent: mutate,
    updateEvent: handleUpdateEvent,
    deleteEvent: handleDeleteEvent,
  };
}

/**
 * Hook for accessing a single event's summary (all participants).
 * @param id Event ID
 */
export function useEventSummary(id: number) {
  const { data, error, isLoading, mutate } = useSWR<EventSummary[]>(
    id ? EVENTS_CACHE_KEYS.EVENT_BY_ID(id.toString()) : null,
    () => fetchEventSummaryById(id),
    swrConfig,
  );

  const handleUpdateEvent = async (input: EventSupaUpdate) => {
    await updateEvent(input);
    mutate();
  };

  const handleDeleteEvent = async () => {
    await deleteEvent(id);
    mutate();
  };

  return {
    eventSummary: data ?? [],
    isLoading,
    error,
    refreshEvent: mutate,
    updateEvent: handleUpdateEvent,
    deleteEvent: handleDeleteEvent,
  };
}

/**
 * Hook for accessing events by user ID
 */
export function useEventsByUserId(userId?: string, startDate?: string, endDate?: string) {
  const cacheKey = userId ? EVENTS_CACHE_KEYS.EVENTS_BY_USER_ID(userId, startDate, endDate) : null;

  const { data, error, isLoading, mutate } = useSWR<EventSummary[]>(
    cacheKey,
    () => fetchEventsByUserId(userId!, startDate, endDate),
    swrConfig,
  );

  return {
    events: data || [],
    isLoading,
    error,
    refreshEvents: mutate,
  };
}

export function useEventDetailsByUserId(userId?: string, before?: string, after?: string) {
  const cacheKey = userId
    ? EVENTS_CACHE_KEYS.EVENT_DETAILS_BY_USER_ID(userId, before, after)
    : null;

  const { data, error, isLoading, mutate } = useSWR<EventDetails[]>(
    cacheKey,
    () => fetchEventsDetails({ userId, after, before }),
    swrConfig,
  );

  return {
    events: data || [],
    isLoading,
    error,
    refreshEvents: mutate,
  };
}

/**
 * Hook for accessing events by staff ID
 */
export function useEventsByStaffId(staffId: string, startDate?: string, endDate?: string) {
  const cacheKey = staffId
    ? EVENTS_CACHE_KEYS.EVENTS_BY_STAFF_ID(staffId, startDate, endDate)
    : null;

  const { data, error, isLoading, mutate } = useSWR(
    cacheKey,
    () => fetchEventsByStaffId(staffId, startDate, endDate),
    swrConfig,
  );

  return {
    events: data || [],
    isLoading,
    error,
    refreshEvents: mutate,
  };
}

/**
 * Hook for accessing events by transaction ID
 */
export function useEventsByTransactionId(transactionId: number) {
  const cacheKey = transactionId
    ? EVENTS_CACHE_KEYS.EVENTS_BY_TRANSACTION_ID(transactionId.toString())
    : null;

  const { data, error, isLoading, mutate } = useSWR<EventSummary[]>(
    cacheKey,
    () => fetchEventsByTransactionId(transactionId),
    swrConfig,
  );

  return {
    events: data || [],
    isLoading,
    error,
    refreshEvents: mutate,
  };
}

// --- New SWR Hooks for Detailed Events ---

export interface EventsDetailsPaginationOptions {
  page: number;
  pageSize: number;
  sortModel?: GridSortModel;
  status?: AdminBookingStatus;
  userId?: string;
  staffId?: string;
}

export async function fetchPaginatedEventsDetails(options: EventsDetailsPaginationOptions) {
  const { page, pageSize, sortModel, status, userId, staffId } = options;
  const rangeFrom = (page - 1) * pageSize;
  const rangeTo = rangeFrom + pageSize - 1;

  let query = supabaseClient.from("events").select(eventDetailsSelect, { count: "exact" });

  const eventIdFilters: number[][] = [];

  if (userId) {
    const { data: eventIdsData, error: eventIdsError } = await supabaseClient
      .from("event_participants")
      .select("event_id")
      .eq("user_id", userId);
    if (eventIdsError) throw eventIdsError;
    eventIdFilters.push(eventIdsData.map((p) => p.event_id));
  }

  if (staffId) {
    const { data: eventIdsData, error: eventIdsError } = await supabaseClient
      .from("event_participants")
      .select("event_id")
      .eq("staff_id", staffId);
    if (eventIdsError) throw eventIdsError;
    eventIdFilters.push(eventIdsData.map((p) => p.event_id));
  }

  if (status === "client-unpaid") {
    const { data: unpaidTxIdsData, error: txError } = await supabaseClient
      .from("transactions")
      .select("transaction_id")
      .eq("status", "UNPAID")
      .eq("transaction_type", "CHARGE");

    if (txError) throw txError;
    const unpaidTxIds = unpaidTxIdsData.map((t) => t.transaction_id);

    if (unpaidTxIds.length > 0) {
      const { data: eventIdsData, error: linkError } = await supabaseClient
        .from("event_transaction_links")
        .select("event_id")
        .in("transaction_id", unpaidTxIds);
      if (linkError) throw linkError;
      eventIdFilters.push(eventIdsData.map((l) => l.event_id));
    } else {
      eventIdFilters.push([]);
    }
  } else if (status === "tutor-unpaid") {
    console.warn("Tutor unpaid filter is not implemented yet");
  }

  if (eventIdFilters.length > 0) {
    const intersectingEventIds = eventIdFilters.reduce((a, b) => a.filter((c) => b.includes(c)));
    if (intersectingEventIds.length === 0) {
      return { events: [], count: 0 };
    }
    query = query.in("event_id", intersectingEventIds);
  }

  if (sortModel && sortModel.length > 0) {
    const sortItem = sortModel[0];
    if (sortItem.field && sortItem.sort) {
      query = query.order(sortItem.field, { ascending: sortItem.sort === "asc" });
    }
  } else {
    query = query.order("start_date", { ascending: false });
  }

  query = query.range(rangeFrom, rangeTo);

  const { data, error, count } = await query;

  if (error) {
    console.error("Error fetching paginated events details", error);
    throw error;
  }

  return { events: data ?? [], count: count ?? 0 };
}

export function usePaginatedEventsDetails(options: EventsDetailsPaginationOptions) {
  const { page, pageSize, sortModel, status, userId, staffId } = options;
  const cacheKey = [
    EVENTS_CACHE_KEYS.EVENTS,
    "paginated-details",
    page,
    pageSize,
    JSON.stringify(sortModel),
    status,
    userId,
    staffId,
  ]
    .filter(Boolean)
    .join("-");

  const { data, error, isLoading, mutate } = useSWR(
    cacheKey,
    () => fetchPaginatedEventsDetails(options),
    swrConfig,
  );

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
}

export function useEventDetails(id?: number) {
  const { data, error, isLoading, mutate } = useSWR<EventDetails | null>(
    id ? `event-details-${id}` : null,
    () => fetchEventDetails(id!),
    swrConfig,
  );

  return {
    eventDetails: data,
    isLoading,
    error,
    refresh: mutate,
  };
}

export function useEventsDetails(filters: EventsDetailsFilters) {
  const cacheKey = `events-details-${filters.userId || "all"}-${filters.staffId || "all"}-${filters.limit || -1}-${filters.skip || 0}`;
  const { data, error, isLoading, mutate } = useSWR<EventDetails[]>(
    cacheKey,
    () => fetchEventsDetails(filters),
    swrConfig,
  );

  return {
    eventsDetails: data,
    isLoading,
    error,
    refresh: mutate,
  };
}

const UsersSchema = z.object({
  user_id: z.string().uuid(),
  email: z.string().email().nullable(),
  first_name: z.string(),
  last_name: z.string(),
  cognito_sub: z.string().nullable(),
  phone: z.string().nullable(),
  is_child: z.boolean(),
  is_parent: z.boolean(),
  is_active: z.boolean(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  has_onboarded: z.boolean(),
  grade: z.string().nullable(),
  school: z.string().nullable(),
  crm_notes: z.string().nullable(),
  grade_updated_at: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(), // Assuming YYYY-MM-DD
  subjects: z.array(z.string()).nullable(),
});

const EventParticipantsSchema = z.object({
  participant_id: z.number().int(),
  event_id: z.number().int(),
  user_id: z.string().uuid().nullable(),
  staff_id: z.string().uuid().nullable(),
  role: z.enum(["tutor", "student", "parent"]), // Restrict to known roles
  attendance: z.enum(["attended", "not_attended", "late", "cancelled", "no_show"]).nullable(),
  created_at: z.iso.datetime({ offset: true }),
  users: UsersSchema.nullable(),
  staff: SimpleStaffSchema.nullable(),
});

const ServiceTypesSchema = z.object({
  service_type_id: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  base_price: z.number(),
  base_staff_rate: z.number(),
  min_duration: z.number().int(),
  max_duration: z.number().int(),
  is_active: z.boolean(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  tutor_can_add_discount: z.boolean(),
  category: z.string(),
});

const TransactionsSchema = z.object({
  transaction_id: z.number().int(),
  amount: z.number(),
  description: z.string(),
  transaction_type: z.enum(["CHARGE", "PAYOUT"]),
  charged_at: z.iso.datetime({ offset: true }).nullable(), // Nullable based on 'UNPAID' status, but present in sample
  notes: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  payment_id: z.number().nullable(),
  status: z.enum(["PAID", "UNPAID"]), // Assuming a set of statuses
});

const EventTransactionLinksSchema = z.object({
  link_id: z.number().int(),
  event_id: z.number().int(),
  transaction_id: z.number().int(),
  amount_applied: z.number().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  transactions: TransactionsSchema,
});

// --- Root Schema ---

export const FamilyEventSchema = z.object({
  event_id: z.number().int(),
  title: z.string(),
  description: z.string().nullish(),
  start_date: z.iso.datetime({ offset: true }),
  end_date: z.iso.datetime({ offset: true }),
  attendance: z.enum(["ATTENDED", "NOT_ATTENDED", "CANCELLED"]).nullable(),
  is_taught: z.boolean(),
  url: z.string().nullable(),
  location: z.string().nullable(),
  public_notes: z.string().nullable(),
  private_notes: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
  code: z.string().nullable(),
  service_type_id: z.number().int(),
  service_types: ServiceTypesSchema,
  event_participants: z.array(EventParticipantsSchema),
  event_transaction_links: z.array(EventTransactionLinksSchema),
});

export const FamilyEventsArraySchema = z.array(FamilyEventSchema);

// --- TypeScript Types from Zod ---
export type Staff = z.infer<typeof StaffSchema>;
export type Users = z.infer<typeof UsersSchema>;
export type EventParticipants = z.infer<typeof EventParticipantsSchema>;
export type ServiceTypes = z.infer<typeof ServiceTypesSchema>;
export type Transactions = z.infer<typeof TransactionsSchema>;
export type EventTransactionLinks = z.infer<typeof EventTransactionLinksSchema>;

/**
 * The fully defined type for a single family event.
 * This should replace your original 'FamilyEvents' interface.
 */
export type FamilyEvent = z.infer<typeof FamilyEventSchema>;
/**
 * The type for the array returned by the rpc call.
 */
export type FamilyEvents = z.infer<typeof FamilyEventsArraySchema>;

export function useFamilyEvents(
  familyId?: number | null,
  startsAfter?: string,
  startsBefore?: string,
  is_taught?: boolean,
) {
  const fetcher = async (familyIdNumber: number): Promise<FamilyEvent[]> => {
    if (familyId === -1) {
      throw new Error("Invalid Family Id");
    }
    const res = await supabaseClient.rpc("get_family_events_table", {
      p_family_id: familyIdNumber,
      p_starts_after: startsAfter,
      p_starts_before: startsBefore,
      p_is_taught: is_taught,
    });

    if (res.error) {
      throw res.error;
    }

    const parsed = FamilyEventsArraySchema.safeParse(res.data);
    if (!parsed.success) {
      console.error("Parsing Error", parsed.error);
      logger.error("Zod Error parsing family events", {
        error: parsed.error,
        data: res.data,
        location: "useFamilyEvents(fetcher)",
        source: "zod parsing",
      });
      return [];
    }

    return parsed.data ?? [];
  };

  const {
    data = [],
    error,
    isLoading,
    mutate,
  } = useSWR(
    familyId
      ? EVENTS_CACHE_KEYS.EVENTS_DETAILS_BY_FAMILY_ID(
          familyId,
          startsBefore,
          startsAfter,
          is_taught,
        )
      : null,
    () => fetcher(familyId!),
    swrConfig,
  );

  return {
    events: data || [],
    isLoading,
    error,
    refresh: mutate,
  };
}

export function useAllEvents() {
  const { data, error, isLoading, mutate } = useSWR<EventDetails[]>(
    EVENTS_CACHE_KEYS.EVENTS_ALL,
    () => fetchEventsDetails({ limit: 1000 }), // Fetch up to 1000 events for selection
    swrConfig,
  );

  return {
    events: data || [],
    isLoading,
    error,
    refresh: mutate,
  };
}
